import React, { useEffect, useState } from "react";
import { Hourglass } from "lucide-react";

/**
 * Returns the next Monday 23:59:59 in the viewer's LOCAL time zone.
 * We deliberately compute this client-side so each viewer sees their own
 * Monday end-of-day countdown — keeps the urgency intuitive.
 */
function nextMondayDeadline() {
  const now = new Date();
  const day = now.getDay(); // 0 = Sunday, 1 = Monday
  let daysUntil = (1 - day + 7) % 7;
  // If it's currently Monday and before midnight, the deadline is *today* 23:59
  if (daysUntil === 0 && now.getHours() < 23) {
    // today
  } else if (daysUntil === 0) {
    daysUntil = 7;
  }
  const target = new Date(now);
  target.setDate(now.getDate() + daysUntil);
  target.setHours(23, 59, 59, 0);
  return target;
}

export default function MondayCountdown({ compact = false }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(iv);
  }, []);

  const deadline = nextMondayDeadline();
  const diff = Math.max(0, deadline.getTime() - now);
  const days = Math.floor(diff / 86_400_000);
  const hours = Math.floor((diff % 86_400_000) / 3_600_000);
  const mins = Math.floor((diff % 3_600_000) / 60_000);

  const urgent = diff < 24 * 3600 * 1000;
  const colorCls = urgent ? "text-[#E11D48] border-[#E11D48]" : "text-[#7C3AED] border-[#7C3AED]/40";

  if (compact) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 border px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em] ${colorCls}`}
        data-testid="monday-countdown-compact"
        title={`Monday end-of-day: any unplayed match auto-resolves to a 0-0 draw at ${deadline.toLocaleString()}`}
      >
        <Hourglass className="w-3 h-3" />
        {days}d {hours}h {mins}m TO MONDAY
      </span>
    );
  }

  return (
    <div
      className={`bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-l-2 ${colorCls.includes("FF3B30") ? "border-l-[#E11D48]" : "border-l-[#7C3AED]"} px-4 py-3 inline-flex items-center gap-3`}
      data-testid="monday-countdown"
    >
      <Hourglass className={`w-4 h-4 ${urgent ? "text-[#E11D48]" : "text-[#7C3AED]"}`} />
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// next calibration</div>
        <div className={`font-heading text-xl tracking-wider ${urgent ? "text-[#E11D48]" : "text-[#09090B] dark:text-[#F4F4F5]"}`}>
          {days}D {String(hours).padStart(2, "0")}H {String(mins).padStart(2, "0")}M
        </div>
        <div className="font-mono text-[10px] text-[#71717A] tracking-[0.2em]">
          UNPLAYED MATCHES BEFORE {deadline.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} 23:59 → 0-0 DRAW
        </div>
      </div>
    </div>
  );
}
